import { useCallback, useEffect, useRef } from 'react';

/**
 * The left nav is collapsed to its icon rail by default, and the user's choice (the PrivateRoutes state, stored under
 * this key) has to survive a page refresh.
 */
export const SIDE_MENU_STORAGE_KEY = 'visibleSideMenu';

/**
 * Whether the left nav starts collapsed (true) or open (false): what the user left it as, collapsed when nothing was
 * stored or storage is unavailable.
 */
export const readSavedSideMenu = (): boolean => {
    try {
        return window.localStorage.getItem(SIDE_MENU_STORAGE_KEY) !== 'false';
    } catch {
        return true;
    }
};

/**
 * The onCollapse of the antd Sider, without the call it makes by itself when it mounts.
 *
 * With a breakpoint the Sider checks the screen width in its mount effect and, when its collapsed state disagrees
 * with the breakpoint, calls onCollapse(..., 'responsive'). On a desktop screen that was a call to open the menu, so
 * every page load (a refresh, or a bookmark) opened the nav full width whatever it was. The menu has to look the same
 * after a refresh, so that one call is dropped. The Sider is a child of the component using this hook, so its mount
 * effect runs before the effect here marks the Sider as mounted; later responsive calls, from the window crossing the
 * breakpoint, and clicks on the trigger go through.
 */
export const useSiderCollapse = (onCollapseMenu: () => void) => {
    const mounted = useRef(false);
    useEffect(() => {
        mounted.current = true;
        return () => {
            mounted.current = false;
        };
    }, []);
    return useCallback(
        (_collapsed: boolean, type?: 'clickTrigger' | 'responsive') => {
            if (type === 'responsive' && !mounted.current) return;
            onCollapseMenu();
        },
        [onCollapseMenu],
    );
};
